// REACT
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// MATERIAL UI
import { Button, Grid, Skeleton, Stack, Typography } from "@mui/material";

// COMPONENTS
import Title from "src/components/common/Title";
import { AccountList } from "./AccountList";
import { BankForm } from "./BankForm";
import { Complete } from "./Complete";
import { PaymentContainer } from "./PaymentContainer";
import { Summary } from "./Summary";

// STYLES
import { PaymentStyles } from "./Payment.styles";

// REDUX
import { useDispatch } from "react-redux";
import {
  initialState,
  setBeneficiaryState,
  setStep,
  useBeneficiarySlice,
} from "src/store/features/beneficiary.slice";

// HOOKS
import { useBankDetails } from "src/utils/hooks/banking/useBankDetails";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

export const Payment = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const classes = PaymentStyles();

  /*********************
   * BENEFICIARY STATE *
   *********************/
  const { step } = useBeneficiarySlice();

  /*************
   * HOOK DATA *
   *************/
  const { bankAccounts, accountStates, isInitialLoading, toggleAccountVisibility } =
    useBankDetails();

  /************
   * HANDLERS *
   ************/
  const handleAddDetails = () => {
    dispatch(setStep("AddDetails"));
    trackEvent("Client clicked on Add Bank Details", {}, "Payment");
  };

  // scroll to the top of the page when the step changes for UX
  // having to use type assertion here even though instant is a valid
  // type option. TS complains it doesn't exist for some reason
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [step]);

  // resets the state when user navigates away from the payments page
  useEffect(() => {
    dispatch(setBeneficiaryState(initialState));
  }, [location.pathname]);

  if (isInitialLoading) {
    return (
      <PaymentContainer>
        <Stack spacing={4} className={classes.loadingContainer}>
          <Skeleton variant="text" width="100%" height={60} />
          {Array.from({ length: 5 }, (_, i: number) => (
            <Skeleton key={i} variant="rectangular" width="100%" height={60} />
          ))}
        </Stack>
      </PaymentContainer>
    );
  }

  if (step === "AddDetails") {
    return (
      <PaymentContainer>
        <BankForm />
      </PaymentContainer>
    );
  }

  if (step === "Summary") {
    return (
      <PaymentContainer>
        <Summary />
      </PaymentContainer>
    );
  }

  if (step === "Complete") {
    return (
      <PaymentContainer>
        <Complete />
      </PaymentContainer>
    );
  }

  if (!bankAccounts?.length) {
    return (
      <PaymentContainer>
        <Grid container height="70vh">
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            alignContent="center"
            flexDirection="column"
            sx={{ padding: "0 24px" }}
            spacing={3}>
            <Grid item>
              <Typography className={classes.title}>
                You currently do not have any bank details saved with us.
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.text}>
                Save the trouble of emailing your details over to your broker! Enter your bank
                details and store it securely here with us.
              </Typography>
            </Grid>
            <Grid item>
              <Button
                disableRipple
                variant="outlined"
                className={classes.addDetailsButton}
                onClick={handleAddDetails}>
                Add Bank Details
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <Grid className={classes.bankDetailsContainer}>
        <Title title={{ partB: "Bank Accounts" }} />
        <Grid container item display="flex" justifyContent="center" style={{ paddingLeft: "8px" }}>
          <Grid item sm={6} paddingBottom="16px">
            <Typography sx={{ fontSize: 14, color: "#5C6469" }}>
              Save your bank account with us. This will be stored across all your accounts. If you
              are looking to modify/remove your bank details, <b>or to withdraw</b>, please contact
              your broker.
            </Typography>
          </Grid>
          <Grid item sm={6} alignItems="end" textAlign="end">
            <Button
              disableRipple
              variant="outlined"
              className={classes.addDetailsButton}
              onClick={handleAddDetails}>
              Add New
            </Button>
          </Grid>
        </Grid>
        <AccountList accounts={accountStates} toggleVisibility={toggleAccountVisibility} />
      </Grid>
    </PaymentContainer>
  );
};
